import React from 'react';
import { useSelector } from 'react-redux';

import { AwardJapanStandard } from '@travel/icons/award';
import JapanStandardPopup from '@travel/traveler-core/components/JapanStandardPopup';
import RakutenAwardPopup from '@travel/traveler-core/components/RakutenAwardPopup';
import { useDialogHandler, useTranslation } from '@travel/traveler-core/hooks';
import { RakutenAward, Tooltip } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import DataLayer from 'components/DataLayer';

import { getDetailSummary } from 'store/providerInformation/selectors';

import { Translate } from 'core/translate';

import styles from './japanQualityAndAward.module.scss';

type Props = {
  /** parent page name for RAT popups */
  pageName: string;
  /** String defining current provider id */
  pageIdentifier: string;
  wrapperClassName?: string;
};

const JapanQualityAndAward = (props: Props) => {
  const { pageName, pageIdentifier, wrapperClassName } = props;

  const detailSummary = useSelector(getDetailSummary(pageIdentifier));
  const { awards, isJapanStandard } = detailSummary;
  const {
    isOpen: isAwardsPopupOpen,
    onOpen: onAwardsPopupOpen,
    onClose: onAwardsPopupClose,
  } = useDialogHandler(false);
  const {
    isOpen: isJapanStandardPopupOpen,
    onOpen: onJapanStandardPopupOpen,
    onClose: onJapanStandardPopupClose,
  } = useDialogHandler(false);

  const japanStandardPopupAriaLabel = useTranslation({
    id: 'Traveler_Common.Japan_Quality.Name',
  });

  const rakutenAwards = awards
    ? awards
        .filter((award) => award.image)
        .map((award) => (
          <RakutenAward
            key={award?.name}
            title={award?.name}
            image={award?.image}
            imageClassName={styles.awardImage}
            data-testid="providerInformation-japanQualityAndAward-firstAward"
          />
        ))
    : [];

  const shouldDisplayJapanQualityAndAward = Boolean(
    isNotEmptyArray(rakutenAwards) || isJapanStandard,
  );
  const {
    isOpen: isJapanStandardTooltipOpen,
    onOpen: onJapanStandardTooltipOpen,
    onClose: onJapanStandardTooltipClose,
  } = useDialogHandler(false);
  const {
    isOpen: isRakutenAwardTooltipOpen,
    onOpen: onRakutenAwardTooltipOpen,
    onClose: onRakutenAwardTooltipClose,
  } = useDialogHandler(false);

  return shouldDisplayJapanQualityAndAward ? (
    <div
      className={cx(styles.awardWrapper, wrapperClassName)}
      data-testid="providerInformation-japanQualityAndAward-wrapper"
    >
      <RakutenAwardPopup isOpen={isAwardsPopupOpen} onClose={onAwardsPopupClose} />
      {isAwardsPopupOpen && (
        <DataLayer
          pageName={`${pageName}:rakuten_award_information_popup`}
          pageType="shop_item"
          siteSection="hotel"
        />
      )}
      <JapanStandardPopup
        isOpen={isJapanStandardPopupOpen}
        isUsedForSEO={true}
        onCloseCallback={onJapanStandardPopupClose}
      />
      {isJapanStandardPopupOpen && (
        <DataLayer
          pageName={`${pageName}:japan_quality_information_popup`}
          pageType="shop_item"
          siteSection="hotel"
        />
      )}
      {isJapanStandard && (
        <button
          data-testid="providerInformation-japanQualityAndAward-japanStandardAward"
          aria-label={japanStandardPopupAriaLabel}
          className={styles.japanStandardAward}
          onClick={onJapanStandardPopupOpen}
          onMouseEnter={onJapanStandardTooltipOpen}
          onMouseLeave={onJapanStandardTooltipClose}
          onFocus={onJapanStandardTooltipOpen}
          onBlur={onJapanStandardTooltipClose}
        >
          <AwardJapanStandard />
          {isJapanStandardTooltipOpen && (
            <Tooltip className={styles.toolTip}>
              <Translate id="Traveler_Common.Japan_Quality.Hover" />
            </Tooltip>
          )}
        </button>
      )}
      {isNotEmptyArray(rakutenAwards) && (
        <button
          className={styles.japanStandardAward}
          data-testid="providerDetailBox-rakutenAward"
          aria-label={japanStandardPopupAriaLabel}
          onClick={onAwardsPopupOpen}
          onMouseEnter={onRakutenAwardTooltipOpen}
          onMouseLeave={onRakutenAwardTooltipClose}
          onFocus={onRakutenAwardTooltipOpen}
          onBlur={onRakutenAwardTooltipClose}
        >
          {rakutenAwards[0]}
          {isRakutenAwardTooltipOpen && (
            <Tooltip className={styles.toolTip}>
              <Translate id="Traveler_Common.Awards.Hover" />
            </Tooltip>
          )}
        </button>
      )}
    </div>
  ) : null;
};

export default JapanQualityAndAward;
